import type React from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { BarSpan, BarTap } from "./DensityChart.lib";
import { barTapAtPoint } from "./DensityChart.lib";
import type { WindowDragStrip } from "./useDensityStrip";
import type { DragDraft } from "./useDragDraft";
import { useWindowEdgePan } from "./useWindowEdgePan";
import type { DragOrigin } from "./windowDragOrigin";
import { dragFromOrigin, pressOrigin } from "./windowDragOrigin";
import type { WindowDragMode } from "./windowSpan.lib";
import { isSameSpan, shiftSpan } from "./windowSpan.lib";

const TAP_SLOP_PX = 4;

interface UseWindowPointerParams {
    span: BarSpan;
    barCount: number;
    minBar: number;
    strip: WindowDragStrip;
    trackRef: React.RefObject<HTMLElement | null>;
    dragDraft: DragDraft;
    onChange: (span: BarSpan) => void;
    onBarTap?: (tap: BarTap) => void;
}

/**
 * The pointer half of `useWindowDrag`: presses, moves the draft under the
 * pointer while panning near the edges, and snaps and commits on release.
 * A press on the body that never moves taps a bar; a cancelled press, or
 * one whose bars change unit (a resize, say), is dropped, along with the
 * click that ends a dropped draw. A drawn range that lands on the window
 * changes nothing.
 */
export function useWindowPointer({
    span,
    barCount,
    minBar,
    strip,
    trackRef,
    dragDraft,
    onChange,
    onBarTap,
}: UseWindowPointerParams) {
    const { unit, firstBar, offset, edgeRef, panByPx, holdBars, releaseBars } =
        strip;
    const { start: visibleStart, end: visibleEnd } = strip.visible;
    const {
        draftRef,
        updateDraft,
        startDraft,
        settle,
        resetDraft,
        updatePreview,
    } = dragDraft;
    const [pointerMode, setPointerMode] = useState<WindowDragMode | null>(null);
    const originRef = useRef<DragOrigin | null>(null);
    const didDrawRef = useRef(false);
    const { follow: followEdge, stop: stopEdgePan } = useWindowEdgePan({
        edgeRef,
        onEdgePan: panByPx,
        originRef,
        draftRef,
    });

    const handlePointerDown = useCallback(
        (mode: WindowDragMode, event: React.PointerEvent<HTMLElement>) => {
            didDrawRef.current = false;
            const trackRect = trackRef.current?.getBoundingClientRect();
            const origin =
                !originRef.current &&
                event.button === 0 &&
                trackRect &&
                trackRect.width > 0 &&
                pressOrigin({
                    mode,
                    press: event,
                    trackRect,
                    span,
                    unit,
                    barCount,
                    minBar,
                    firstBar,
                    offset,
                    visible: { start: visibleStart, end: visibleEnd },
                });
            if (!origin) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.setPointerCapture(event.pointerId);
            originRef.current = origin;
            holdBars();
            if (mode !== "draw") {
                startDraft(origin.span);
            }
            setPointerMode(mode);
        },
        [
            barCount,
            firstBar,
            holdBars,
            minBar,
            offset,
            span,
            startDraft,
            trackRef,
            unit,
            visibleStart,
            visibleEnd,
        ],
    );

    /** Puts the window under the pointer, counting how far the strip has panned since the press. */
    const dragTo = useCallback(
        (origin: DragOrigin) => {
            if (origin.mode === "draw" && !origin.hasMoved) {
                return;
            }
            const { next, landing } = dragFromOrigin({
                origin,
                firstBar,
                offset,
                barCount,
                minBar,
                visible: { start: visibleStart, end: visibleEnd },
            });
            updateDraft(shiftSpan(next, firstBar));
            if (origin.hasMoved) {
                updatePreview(shiftSpan(landing, firstBar));
            }
        },
        [
            barCount,
            firstBar,
            minBar,
            offset,
            updateDraft,
            updatePreview,
            visibleStart,
            visibleEnd,
        ],
    );

    const releaseOrigin = useCallback(() => {
        originRef.current = null;
        stopEdgePan();
        releaseBars();
        updatePreview(null);
        setPointerMode(null);
    }, [releaseBars, stopEdgePan, updatePreview]);

    useLayoutEffect(() => {
        const origin = originRef.current;
        if (origin && origin.unit !== unit) {
            didDrawRef.current = origin.mode === "draw" && origin.hasMoved;
            releaseOrigin();
            resetDraft();
        }
    }, [unit, releaseOrigin, resetDraft]);

    useLayoutEffect(() => {
        const origin = originRef.current;
        if (origin?.hasMoved) {
            dragTo(origin);
        }
    }, [dragTo]);

    const handlePointerMove = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            const origin = originRef.current;
            if (!origin || origin.pointerId !== event.pointerId) {
                return;
            }
            origin.lastClientX = event.clientX;
            if (
                !origin.hasMoved &&
                Math.abs(event.clientX - origin.clientX) > TAP_SLOP_PX
            ) {
                origin.hasMoved = true;
                if (origin.mode === "draw") {
                    startDraft(origin.span);
                }
            }
            dragTo(origin);
            if (origin.hasMoved) {
                followEdge(event.clientX);
            }
        },
        [dragTo, followEdge, startDraft],
    );

    const takeOrigin = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            const origin = originRef.current;
            if (!origin || origin.pointerId !== event.pointerId) {
                return null;
            }
            releaseOrigin();
            return origin;
        },
        [releaseOrigin],
    );

    const handlePointerEnd = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            const origin = takeOrigin(event);
            if (!origin) {
                return;
            }
            const hasMoved =
                origin.hasMoved ||
                Math.abs(event.clientX - origin.clientX) > TAP_SLOP_PX;
            if (!hasMoved) {
                resetDraft();
                if (origin.mode === "move") {
                    onBarTap?.(
                        barTapAtPoint({
                            rect: origin.trackRect,
                            point: event,
                            barCount,
                        }),
                    );
                }
                return;
            }
            origin.lastClientX = event.clientX;
            const { landing: snapped } = dragFromOrigin({
                origin,
                firstBar,
                offset,
                barCount,
                minBar,
                visible: { start: visibleStart, end: visibleEnd },
            });
            const isDraw = origin.mode === "draw";
            settle(shiftSpan(snapped, firstBar));
            didDrawRef.current = isDraw;
            if (
                !isSameSpan(
                    snapped,
                    isDraw ? span : shiftSpan(origin.span, -firstBar),
                )
            ) {
                onChange(snapped);
            }
        },
        [
            barCount,
            firstBar,
            minBar,
            offset,
            onChange,
            onBarTap,
            resetDraft,
            settle,
            span,
            takeOrigin,
            visibleStart,
            visibleEnd,
        ],
    );

    const handlePointerCancel = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            if (takeOrigin(event)) {
                resetDraft();
            }
        },
        [resetDraft, takeOrigin],
    );

    const isDragging = useCallback(() => originRef.current !== null, []);

    return {
        /** The handle or body the pointer holds. */
        pointerMode,
        isDragging,
        handlePointerDown,
        handlePointerMove,
        handlePointerEnd,
        handlePointerCancel,
        /** Whether a click is the one that ends a drawn range; asking clears it, so only that click is skipped. */
        consumeClickAfterDraw: () => {
            const didDraw = didDrawRef.current;
            didDrawRef.current = false;
            return didDraw;
        },
    };
}
